import { round } from "~/functions/round"
import { breakpoints, colors, eases, grid } from "~/styles/exports/constants"

// Breakpoints

export type Target = "xxs" | "xs" | "sm" | "md" | "lg" | "xl"

type SharedBreakpoint = {
  targetWindowViewWidth: number
  columns: number
  containerPadding: number
}

type BreakpointWithoutMinWidth = { minWidth: undefined } & SharedBreakpoint

type Breakpoint = { minWidth: number } & SharedBreakpoint

type Breakpoints = {
  [key in Target]: key extends "xxs" ? BreakpointWithoutMinWidth : Breakpoint
}

const remToNumber = (value: string) => Number(value.replace("rem", "")) * 10

const BREAKPOINT_MIN_WIDTH_XS = breakpoints.values.xs
const BREAKPOINT_MIN_WIDTH_SM = breakpoints.values.sm
const BREAKPOINT_MIN_WIDTH_MD = breakpoints.values.md
const BREAKPOINT_MIN_WIDTH_LG = breakpoints.values.lg
const BREAKPOINT_MIN_WIDTH_XL = breakpoints.values.xl

const GRID_COLUMNS = grid.values.columns
const GRID_COLUMNS_SM = grid.values["columns-sm"]
const GRID_COLUMNS_MD = grid.values["columns-md"]

const CONTAINER_PADDING = remToNumber(
  grid.values["container-padding"] as string,
)
const CONTAINER_PADDING_SM = remToNumber(
  grid.values["container-padding-sm"] as string,
)
const CONTAINER_PADDING_MD = remToNumber(
  grid.values["container-padding-md"] as string,
)
const CONTAINER_PADDING_LG = remToNumber(
  grid.values["container-padding-lg"] as string,
)

export const BREAKPOINTS: Breakpoints = {
  xxs: {
    minWidth: undefined,
    targetWindowViewWidth: BREAKPOINT_MIN_WIDTH_XS as number,
    columns: GRID_COLUMNS as number,
    containerPadding: CONTAINER_PADDING,
  },
  xs: {
    minWidth: BREAKPOINT_MIN_WIDTH_XS as number,
    targetWindowViewWidth: BREAKPOINT_MIN_WIDTH_XS as number,
    columns: GRID_COLUMNS_SM as number,
    containerPadding: CONTAINER_PADDING,
  },
  sm: {
    minWidth: BREAKPOINT_MIN_WIDTH_SM as number,
    targetWindowViewWidth: BREAKPOINT_MIN_WIDTH_SM as number,
    columns: GRID_COLUMNS_SM as number,
    containerPadding: CONTAINER_PADDING_SM,
  },
  md: {
    minWidth: BREAKPOINT_MIN_WIDTH_MD as number,
    targetWindowViewWidth: BREAKPOINT_MIN_WIDTH_MD as number,
    columns: GRID_COLUMNS_MD as number,
    containerPadding: CONTAINER_PADDING_MD as number,
  },
  lg: {
    minWidth: BREAKPOINT_MIN_WIDTH_LG as number,
    targetWindowViewWidth: BREAKPOINT_MIN_WIDTH_LG as number,
    columns: GRID_COLUMNS_MD as number,
    containerPadding: CONTAINER_PADDING_LG as number,
  },
  xl: {
    minWidth: BREAKPOINT_MIN_WIDTH_XL as number,
    targetWindowViewWidth: BREAKPOINT_MIN_WIDTH_XL as number,
    columns: GRID_COLUMNS_MD as number,
    containerPadding: CONTAINER_PADDING_MD as number,
  },
} as const

// Colors

export const COLORS = {
  "color-white": colors.values.white,
  "color-black": colors.values.black,
  "color-primary": colors.values.primary,
  "color-highlight": colors.values.highlight,
  "color-secondary": colors.values.secondary,
  "color-text": colors.values.text,
} as const

export type Color = keyof typeof COLORS

// Eases

export const EASE_LINEAR = `cubic-bezier(${eases.values.linear})`
export const EASE_OUT = `cubic-bezier(${eases.values.out})`
export const EASE_IN_OUT = `cubic-bezier(${eases.values["in-out"]})`

// Helpers

/**
 * Helper to get total columns width. This returns a calculation relative to the
 * target viewport width for the given `target`.
 */
export const cssColumnsToVwAtTarget = (total: number, target: Target) => {
  const breakpoint = BREAKPOINTS[target]

  // Calculate available width after accounting for container padding
  const targetWindowViewWidthWithoutPadding =
    breakpoint.targetWindowViewWidth - breakpoint.containerPadding * 2

  // Calculate the width of a single column
  const singleColumnWidth =
    targetWindowViewWidthWithoutPadding / breakpoint.columns

  // Then get total width relative to total columns given
  const totalWidth = singleColumnWidth * total

  const totalWidthAsVw = (totalWidth / breakpoint.targetWindowViewWidth) * 100

  return `${round(totalWidthAsVw)}vw`
}

export const getImageSizes = (cols: number) => {
  return {
    base: "100vw",
    sm: cssColumnsToVwAtTarget(cols, "sm"),
    md: cssColumnsToVwAtTarget(cols, "md"),
    lg: cssColumnsToVwAtTarget(cols, "lg"),
    xl: cssColumnsToVwAtTarget(cols, "xl"),
  }
}
